import { DataZoomComponentOption } from 'echarts';
import { DateFormats } from '../../enums/date-formats.js';
import { DateFormatter } from '../../utils/date-formatter.js';
import {
  LTTB_ALGORITM_1920,
  LTTB_ALGORITM_3000,
  // LTTB_ALGORITM_5000,
  GET_LTTB_DATA_STEP_1,
  GET_LTTB_DATA_STEP_2,
  // GET_LTTB_DATA_STEP_3,
  DEFAULT_INDENT,
  DATA_ZOOM,
  CHART_INDENTS,
  WIDTH_HEIGHT_SLIDER,
  DATA_ZOOM_ID,
} from './constants.js';
import { SourceChartDataType } from './types.js';

// const lttbDownAlgoritm = (data: SourceChartDataType[], threshold: number) => {
//   if (data.length <= threshold) return data;

//   const sampled = [];
//   const bucketSize = Math.floor(data.length / threshold);

//   for (let i = 0; i < threshold; i++) {
//     const bucket = data.slice(i * bucketSize, (i + 1) * bucketSize);
//     let bestPoint = bucket[0];
//     let maxVariance = -Infinity;

//     for (const point of bucket) {
//       const variance = Object.keys(point)
//         .filter((key) => key !== 'time' && typeof point[key] === 'number')
//         .reduce((sum, key) => sum + Math.abs(point[key]), 0);

//       if (variance > maxVariance) {
//         maxVariance = variance;
//         bestPoint = point;
//       }
//     }

//     sampled.push(bestPoint);
//   }

//   const restOfData = data.slice(threshold * bucketSize);

//   for (let i = 0; i < restOfData.length; i++) {
//     const bucket = restOfData.slice(i * 1, (i + 1) * 1);
//     let bestPoint = bucket[0];
//     let maxVariance = -Infinity;

//     for (const point of bucket) {
//       const variance = Object.keys(point)
//         .filter((key) => key !== 'time' && typeof point[key] === 'number')
//         .reduce((sum, key) => sum + Math.abs(point[key]), 0);

//       if (variance > maxVariance) {
//         maxVariance = variance;
//         bestPoint = point;
//       }
//     }

//     sampled.push(bestPoint);
//   }

//   return sampled;
// };

const lttbDownAlgoritmV2 = (data: SourceChartDataType[], threshold: number): SourceChartDataType[] => {
  if (data.length <= threshold) return data;

  const sampled: Record<number, any> = {};
  const bucketSize = Math.floor(data.length / threshold);

  for (let i = 0; i < threshold; i++) {
    const bucket = data.slice(i * bucketSize, (i + 1) * bucketSize);
    const bestPointsByKey: Record<string, { time: number; value: number }> = {};
    const lowestPointsByKey: Record<string, { time: number; value: number }> = {};

    for (const point of bucket) {
      const { time, ...rest } = point;
      for (const [key, value] of Object.entries(rest)) {
        if (typeof value !== 'number') continue;

        if (!bestPointsByKey[key] || value > bestPointsByKey[key].value) {
          bestPointsByKey[key] = { time, value };
        }
        if (!lowestPointsByKey[key] || value < lowestPointsByKey[key].value) {
          lowestPointsByKey[key] = { time, value };
        }
      }
    }

    for (const [key, { time, value }] of Object.entries(bestPointsByKey)) {
      if (!sampled[time]) sampled[time] = { time };
      sampled[time][key] = value;
    }

    for (const [key, { time, value }] of Object.entries(lowestPointsByKey)) {
      if (!sampled[time]) sampled[time] = { time };
      sampled[time][key] = value;
    }
  }

  const restOfData = data.slice(threshold * bucketSize);

  for (const point of restOfData) {
    const { time, ...rest } = point;

    if (!sampled[time]) {
      sampled[time] = { time };
    }

    for (const [key, value] of Object.entries(rest)) {
      if (typeof value === 'number') {
        sampled[time][key] = value;
      }
    }
  }

  return Object.values(sampled).sort((a: any, b: any) => a.time - b.time);
};

export const getLttbData = (visibleDataLength: number, data: SourceChartDataType[]) => {
  if (visibleDataLength > GET_LTTB_DATA_STEP_1) {
    return lttbDownAlgoritmV2(data, LTTB_ALGORITM_1920);
  } else if (visibleDataLength > GET_LTTB_DATA_STEP_2) {
    return lttbDownAlgoritmV2(data, LTTB_ALGORITM_3000);
  } else {
    return data;
  }
};

export const getDefaultDataZoom = (clampedStartZoomY: number, clampedEndZoomY: number) => {
  const defaultDataZoom: DataZoomComponentOption[] = [
    {
      id: DATA_ZOOM_ID.INSIDE_Y,
      type: 'inside',
      yAxisIndex: [0],
      zoomLock: false,
      start: DEFAULT_INDENT.START,
      end: DEFAULT_INDENT.END,
      filterMode: 'none',
    },
    {
      id: DATA_ZOOM_ID.INSIDE_X,
      type: 'inside',
      xAxisIndex: [0, 1],
      zoomLock: false,
      start: DEFAULT_INDENT.START,
      end: DEFAULT_INDENT.END,
      filterMode: 'filter',
    },
    {
      id: DATA_ZOOM_ID.SLIDER_X,
      type: 'slider',
      xAxisIndex: [0, 1],
      start: DEFAULT_INDENT.START,
      end: DEFAULT_INDENT.END,
      minSpan: DATA_ZOOM.X.MIN_SPAN,
      maxSpan: DATA_ZOOM.X.MAX_SPAN,
      bottom: `${CHART_INDENTS.SLIDER.SLIDER_X.BOTTOM}px`,
      height: `${WIDTH_HEIGHT_SLIDER}px`,
      left: `${CHART_INDENTS.SLIDER.SLIDER_X.LEFT}%`,
      right: `${CHART_INDENTS.SLIDER.SLIDER_X.RIGHT}%`,
      labelFormatter: function (value) {
        const date = new Date(value);
        const res = DateFormatter({ date, format: DateFormats['dd-MMM yyyy HH:mm:ss a'] });
        const datePattern = /(\d{1,2}-[a-zA-Z]+ \d{4})(.*)/;

        const match = res.match(datePattern);
        const formattedDate = match ? match[1] : ''; // Example: '17-March 2025'
        const formattedTime = match ? match[2] : ''; // Example: ' 09:22:23 AM'

        return `${formattedDate}\n${formattedTime.trim()}`;
      },
      filterMode: 'filter',
    },
    {
      id: DATA_ZOOM_ID.SLIDER_Y,
      type: 'slider',
      yAxisIndex: [0],
      show: true,
      startValue: clampedStartZoomY,
      endValue: clampedEndZoomY,
      minSpan: DATA_ZOOM.Y.MIN_SPAN,
      maxSpan: DATA_ZOOM.Y.MAX_SPAN,
      right: `${CHART_INDENTS.SLIDER.SLIDER_Y.RIGHT}px`,
      width: `${WIDTH_HEIGHT_SLIDER}px`,
      filterMode: 'none',
    },
  ];

  return defaultDataZoom;
};
